/* text.control_adapter.js */

"use strict";

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const InputPasswordControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputPassword;
const StyleUtil = require("../StyleUtil");

/**
 * @event TextControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */
/**
 * @event TextControlAdapter#InitializationComplete
 * @description This event is fired when the control has completely Initialized.
 * @type {Object}
 * @property initializedValue {string} The initialized "starting" value
 */

/**
 * @class TextControlAdapter
 * @classdesc exposes the gl_textfield as a Form Control.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires TextControlAdapter#ValueChanged
 */
module.exports = function TextAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    //call Event Emitter Constructor
    EventEmitter.call(this);
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);

    //The name of the control
    this.name = formCtrlDef.name;
    var _scope;
    this.loadDeferred = undefined;
    this._required = false;
    this._oldInvalidStatus = false;
    this._validationMessages = [];

    /**
     * @returns {string|Array} the data associated with this control.
     */
    this.getValue = function () {
        var val = formCtrlDef.value;
        if (_scope.textApi && _scope.textApi.getValue) {
            val = _scope.textApi.getValue();
        }
        if (Array.isArray(val)) {
            val = val.map(function (element) {
                return element.value;
            });
        } else if (_.get(formCtrlDef, "configSettings.fieldType") === "Array") {
            val = val.split(/\r?\n/);
        }
        return val;
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                if (_scope.isTextArea) {
                    self._updateValidityStatus();
                }
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * Returns if the text control value is valid based on given validation config (pattern)
     * @returns {boolean}
     */
    this.isInvalid = function () {
        if (this._multiEditMode && !this.getMultiEditActive()) {
            return false;
        }
        if (_scope.isTextArea) {
            return this._required
                ? _.isEmpty(this.getValue()) || _.isEqual(this.getValue(), [""])
                : false;
        } else {
            return _scope.textApi.invalid;
        }
    };

    /**
     * Returns validation messages associated with underlying control based on given validation config (pattern)
     * @returns {Array#string}
     */
    this.getValidationMessages = function () {
        return _scope.textApi && _scope.textApi.validationMessages
            ? _scope.textApi.validationMessages
            : this._validationMessages;
    };

    /**
     * Set this controls current value.
     * @param val {string|Array} The value to set this control to.
     * @returns {string|Array} the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (val) {
        var result = val;
        if (Array.isArray(val)) {
            result = val
                .map(function (element) {
                    return _.isObject(element) ? element.value : element;
                })
                .join("\n");
        }

        _scope.textApi.setValue(result);
    };

    this.setAutocompleteHint = function (val) {
        let newVal = val ? val : "";
        if (_scope.textApi.setAutocompleteHint) {
            _scope.textApi.setAutocompleteHint(newVal);
        } else {
            logger.warn("Setting autocompleteHint on this control currently not supported");
        }
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {};

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope
     */
    this.$init = function ($scope /*, $timeout*/) {
        _scope = $scope;
        var idName = _.classify(formCtrlDef.name);
        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        StyleUtil.appendClassListToElement(
            $scope.jTypeContext.layout.inlineLabels,
            containingElem[0]
        );

        //Setup API object
        $scope.textApi = {};
        //Setup Settings Object
        let initialValue = formCtrlDef.value;

        if (this._multiEditMode) {
            initialValue = "";
        }
        $scope.textSettings = {
            id: idName, //<--- not sure what this is for
            name: formCtrlDef.name,
            label: formCtrlDef.displayInfo.label,
            title: formCtrlDef.displayInfo.tooltip,
            value: initialValue,
            placeholder: formCtrlDef.displayInfo.placeholder,
            pattern: _.get(formCtrlDef.configSettings, "String.pattern", undefined),
            onValueChange: function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    self.emit("ValueChanged", {
                        name: formCtrlDef.name,
                        containingElem: containingElem,
                        controlAdapter: self,
                        formControlDefinition: formCtrlDef,
                        oldValue: oldVal,
                        newValue: newVal,
                    });
                }
                if (_scope.isTextArea) {
                    self._updateValidityStatus();
                }
            },
            onInvalid: function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    self.emit("Invalid", {
                        name: formCtrlDef.name,
                        containingElem: containingElem,
                        controlAdapter: self,
                        formControlDefinition: formCtrlDef,
                        oldValue: oldVal,
                        newValue: newVal,
                    });
                }
            },
        };

        //if @Display.password show the text field as a password field
        if (_.get(formCtrlDef, "configSettings.Display.password")) {
            $scope.textSettings.type = "password";
        }

        //Setup directive
        var textFieldElem;
        $scope.isTextArea =
            _.get(formCtrlDef, "configSettings.Display.multiline") ||
            _.get(formCtrlDef, "configSettings.Display.text");
        if ($scope.isTextArea) {
            textFieldElem = $(
                '<gl-textarea class="form_entry" api="textApi" settings="textSettings"></gl-textarea>'
            );
        } else {
            if ($scope.textSettings.type === "password") {
                $scope.textApi = new InputPasswordControl($scope.textSettings);
            } else {
                $scope.textApi = new InputTextControl($scope.textSettings);
            }
            $scope.textApi.addListener("InvalidUserInput", $scope.textSettings.onInvalid);
            $scope.textApi.addListener("UserValue", $scope.textSettings.onValueChange);
            textFieldElem = $scope.textApi.getRenderer().getRootElement();
        }

        containingElem.append(textFieldElem);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });

        // Multi-edit should leave the field blank by default
        if (!this._multiEditMode) {
            self.setValue(formCtrlDef.value);
        }

        this.loadDeferred = q.defer();
        return this.loadDeferred.promise;
    };
    /**
     * @type {Array.<String>}
     * @description $inject Annotation By adding the $inject property onto the init function the Angular
     * injection parameters can be specified.
     */
    this.$init.$inject = ["$scope", "$timeout"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope, $timeout) {
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }

        this._required = _.get(formCtrlDef, "validationInfo.required", false);
        if (this._required) {
            this._required = true;
            if (_scope.textApi && _scope.textApi.setRequired) {
                _scope.textApi.setRequired(true);
            }
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        if (!_.isEmpty(formCtrlDef.configData)) {
            this.setValue(formCtrlDef.configData);
        }

        if (_.has(formCtrlDef, "configSettings.Display.text.minLineCount")) {
            let textArea = containingElem.find("textarea");
            let fontSize = parseInt(textArea.css("font-size"));
            let minLineCount = _.get(formCtrlDef, "configSettings.Display.text.minLineCount");
            let height = fontSize * (minLineCount + 1);
            let areaHeight = "" + height + "px";
            textArea.css("height", areaHeight);
        }

        if (
            _.has(formCtrlDef, "configSettings.Display.autocompleteHint") &&
            !_.isNil(formCtrlDef.configSettings.Display.autocompleteHint)
        ) {
            let autocompleteHint = _.get(formCtrlDef, "configSettings.Display.autocompleteHint");
            this.setAutocompleteHint(autocompleteHint);
        }
        if (_scope.isTextArea) {
            this._updateValidityStatus();
        }

        $timeout(() => {
            this.loadDeferred.resolve();
        });
    };

    this.setReadOnly = function (flag) {
        if (
            _.get(formCtrlDef, "configSettings.Display.multiline") ||
            _.get(formCtrlDef, "configSettings.Display.text")
        ) {
            if (flag) {
                containingElem
                    .find(".gl-textarea-input")
                    .attr("readOnly", flag)
                    .addClass("read-only");
            } else {
                containingElem
                    .find(".gl-textarea-input")
                    .attr("readOnly", flag)
                    .removeClass("read-only");
            }
        } else {
            _scope.textApi.setDisabled(flag);
        }
        if (_scope.textApi && _scope.textApi.setRequired && this._required) {
            _scope.textApi.setRequired(!flag);
        }
    };

    this._updateValidityStatus = function () {
        let newInvalidStatus = this.isInvalid();
        if (newInvalidStatus) {
            this._validationMessages = ["Please enter a value."];
            _scope.textApi.setInvalid("Please enter a value");
            _scope.$digest();
        } else {
            this._validationMessages = [];
            _scope.textApi.setValid();
        }
        if (newInvalidStatus !== this._oldInvalidStatus) {
            this.emit("Invalid", {
                name: formCtrlDef.name,
                containingElem: containingElem,
                controlAdapter: this,
                formControlDefinition: formCtrlDef,
                oldValue: this._oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            this._oldInvalidStatus = newInvalidStatus;
        }
    };

    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
